import type { PrivacyRetentionMatrix, StaffPrivacyRequestView } from "@lafayette/schemas";
import type { IconName } from "../ui/icon";

// The words of the privacy screens, in simple English (ASD-STE100). No customer data is in a label.

type Kind = StaffPrivacyRequestView["kind"];
type State = StaffPrivacyRequestView["state"];
type Step = StaffPrivacyRequestView["steps"][number]["step"];
type ErrorCode = NonNullable<StaffPrivacyRequestView["lastErrorCode"]>;
type Reason = StaffPrivacyRequestView["reasonCode"];
type Action = PrivacyRetentionMatrix["rows"][number]["action"];

export const kindLabels: Readonly<Record<Kind, string>> = {
  EXPORT: "Export of the customer's data",
  DELETE: "Deletion of the customer's account and data",
};

export const stateLabels: Readonly<Record<State, string>> = {
  REQUESTED: "Waiting for a decision",
  APPROVED: "Approved, waiting to run",
  EXECUTING: "Running",
  COMPLETED: "Done",
  PARTIALLY_COMPLETED: "Partly done. A step failed and will be tried again.",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

/** An icon next to the words, so the state does not depend on colour (decoration, `aria-hidden`: the words are the state). */
export const stateGlyphs: Readonly<Record<State, IconName>> = {
  REQUESTED: "hourglass",
  APPROVED: "thumbs-up",
  EXECUTING: "play",
  COMPLETED: "checkmark-circle-02",
  PARTIALLY_COMPLETED: "alert-02",
  REJECTED: "cancel-circle",
  CANCELLED: "unavailable",
};

export const reasonLabels: Readonly<Record<Reason, string>> = {
  CUSTOMER_REQUEST: "The customer asked",
  STAFF_ASSISTED: "The customer asked staff for help",
  LEGAL_REQUEST: "A legal request",
  TEST_CUSTOMER: "A test customer",
};

export const stepLabels: Readonly<Record<Step, string>> = {
  SUSPEND: "Stop all processing of the customer",
  MEMORY: "Clear the remembered facts",
  PROFILE: "Clear name, e-mail, phone and profile",
  PREFERENCES: "Delete the settings",
  SAVED: "Clear the saved items",
  TRIPS: "Clear trips and plans",
  REQUESTS: "Clear requests, notes and file records",
  CONVERSATIONS: "Clear the conversation records",
  NOTIFICATIONS: "Clear the notification records",
  EXPORTS: "Delete the export files",
  ANALYTICS: "Opt out of analytics for good",
  STREAM: "Delete the chat channels and the chat user",
  PUSH: "End the push identity",
  STORAGE: "Delete the uploaded files",
  AUTH: "Delete the sign-in account",
  ANALYTICS_VENDOR: "Delete the analytics events at the vendor",
  TOMBSTONE: "End the link to the sign-in account",
  ASSEMBLE: "Build the export file",
};

export const errorCodeLabels: Readonly<Record<ErrorCode, string>> = {
  PROVIDER_UNAVAILABLE: "The provider did not answer. It will be tried again.",
  PROVIDER_TIMEOUT: "The provider was too slow. It will be tried again.",
  PROVIDER_REFUSED: "The provider refused the call. Check the provider setting.",
  PROVIDER_NOT_CONFIGURED: "The provider is not set up in this environment.",
  DATABASE_ERROR: "The database step failed. It will be tried again.",
  EXPORT_TOO_LARGE: "The export is larger than the limit. It cannot be made.",
  INTERNAL: "An internal error. It will be tried again.",
};

export const actionLabels: Readonly<Record<Action, string>> = {
  DELETED: "Deleted",
  ANONYMIZED: "Anonymized (the row stays, with no personal data)",
  KEPT: "Kept",
  PROVIDER_DELETED: "Deleted at the provider",
  NOT_HELD: "Not held by Lafayette",
};

/** The order in which the actions are shown in a confirm dialog: what is removed first, what stays last. */
export const actionOrder: readonly Action[] = [
  "DELETED",
  "ANONYMIZED",
  "PROVIDER_DELETED",
  "KEPT",
  "NOT_HELD",
];

export const stepSummary = (steps: StaffPrivacyRequestView["steps"]): string => {
  if (steps.length === 0) return "No steps yet";
  const done = steps.filter((step) => step.state === "DONE").length;
  const failed = steps.filter((step) => step.state === "FAILED").length;
  return failed > 0
    ? `${done} of ${steps.length} done, ${failed} failed`
    : `${done} of ${steps.length} done`;
};
